// 보행 그래프(노드/엣지) draft의 "치명적 오류면 배포 차단" 검증 + 청크 분할/체크섬 계산.
// 검증 로직은 src/components/admin/MapAuditScreen.jsx의 computeReport()(고립노드/긴엣지/근접
// 중복좌표 검사)와 같은 기준을 쓰되, 여기서는 정식 배포 게이트로 쓰이므로 중복 id·끊어진 참조
// (존재하지 않는 노드를 가리키는 edge)·좌표 누락/범위 이탈도 추가로 막는다.

import { CAMPUS_GRAPH_CHUNK_BYTE_BUDGET } from '../types/campusGraph.js'

const CAMPUS_CENTER = { lat: 37.2099, lng: 126.9757 }
const MAX_LAT_DELTA = 0.05
const MAX_LNG_DELTA = 0.07
const LONG_EDGE_METERS = 200 // MapAuditScreen의 40m "긴 엣지 경고"보다 훨씬 큰 값만 치명적 오류로 취급
const DUPLICATE_COORD_METERS = 1

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

// campusRouting.js의 getDistanceMeters와 동일한 haversine 공식 — 패키지가 src/를 의존하지
// 않도록 이 작은(10줄) 순수 함수만 의도적으로 중복한다(공유 로직 재사용은 셔틀처럼 크고
// 자주 바뀌는 로직에만 적용하고, 이 정도 안정적인 공식은 복제가 더 안전하다).
function distanceMeters(a, b) {
  const R = 6371000
  const toRad = deg => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

export function validateCampusGraphDraft(data) {
  const errors = []
  const warnings = []
  const nodes = Array.isArray(data?.nodes) ? data.nodes : null
  const edges = Array.isArray(data?.edges) ? data.edges : null
  if (!nodes) { errors.push({ field: 'nodes', message: '노드 목록이 없습니다.' }); return { valid: false, errors, warnings } }
  if (!edges) { errors.push({ field: 'edges', message: '엣지 목록이 없습니다.' }); return { valid: false, errors, warnings } }
  if (nodes.length === 0) warnings.push({ field: 'nodes', message: '노드가 하나도 없습니다.' })

  const nodeById = new Map()
  const seenNodeIds = new Set()
  nodes.forEach((node, index) => {
    const id = String(node?.id ?? '').trim()
    if (!id) { errors.push({ index, field: 'id', message: `노드 ${index + 1}: id가 비어 있습니다.` }); return }
    if (seenNodeIds.has(id)) errors.push({ index, id, field: 'id', message: '중복된 노드 id입니다.' })
    seenNodeIds.add(id)
    const lat = Number(node.lat)
    const lng = Number(node.lng)
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
      errors.push({ index, id, field: 'lat/lng', message: '좌표가 없거나 숫자가 아닙니다.' })
    } else if (Math.abs(lat - CAMPUS_CENTER.lat) > MAX_LAT_DELTA || Math.abs(lng - CAMPUS_CENTER.lng) > MAX_LNG_DELTA) {
      errors.push({ index, id, field: 'lat/lng', message: `좌표가 캠퍼스 반경을 크게 벗어났습니다(${lat}, ${lng}).` })
    } else {
      nodeById.set(id, { ...node, id, lat, lng })
    }
  })

  const seenEdgeIds = new Set()
  edges.forEach((edge, index) => {
    const id = String(edge?.id ?? '').trim()
    const from = String(edge?.from ?? '').trim()
    const to = String(edge?.to ?? '').trim()
    if (!id) { errors.push({ index, field: 'id', message: `엣지 ${index + 1}: id가 비어 있습니다.` }); return }
    if (seenEdgeIds.has(id)) errors.push({ index, id, field: 'id', message: '중복된 엣지 id입니다.' })
    seenEdgeIds.add(id)
    if (!from || !to) { errors.push({ index, id, field: 'from/to', message: '출발/도착 노드가 비어 있습니다.' }); return }
    if (from === to) { errors.push({ index, id, field: 'from/to', message: '출발과 도착이 같은 노드입니다.' }); return }
    const fromNode = nodeById.get(from)
    const toNode = nodeById.get(to)
    if (!fromNode) { errors.push({ index, id, field: 'from', message: `존재하지 않는 노드를 참조합니다: ${from}` }); return }
    if (!toNode) { errors.push({ index, id, field: 'to', message: `존재하지 않는 노드를 참조합니다: ${to}` }); return }
    const distance = distanceMeters(fromNode, toNode)
    if (distance >= LONG_EDGE_METERS) errors.push({ index, id, field: 'distance', message: `두 노드 사이 거리가 비정상적으로 깁니다(${Math.round(distance)}m).` })
  })

  // 고립 노드(연결된 엣지가 하나도 없는 노드) — MapAuditScreen과 동일한 기준(degree===0).
  const degree = new Map()
  edges.forEach(edge => {
    const from = String(edge?.from ?? '').trim()
    const to = String(edge?.to ?? '').trim()
    if (nodeById.has(from)) degree.set(from, (degree.get(from) ?? 0) + 1)
    if (nodeById.has(to)) degree.set(to, (degree.get(to) ?? 0) + 1)
  })
  nodeById.forEach((node, id) => {
    if (!degree.get(id)) warnings.push({ id, field: 'isolated', message: `연결된 경로가 없는 고립 노드입니다: ${id}` })
  })

  // 근접 중복 좌표(서로 다른 id인데 1m 이내) — 실수로 겹쳐 찍힌 노드를 잡아낸다.
  const nodeList = [...nodeById.values()]
  for (let i = 0; i < nodeList.length; i += 1) {
    for (let j = i + 1; j < nodeList.length; j += 1) {
      if (distanceMeters(nodeList[i], nodeList[j]) < DUPLICATE_COORD_METERS) {
        warnings.push({ field: 'duplicate-coordinate', message: `${nodeList[i].id}와 ${nodeList[j].id}가 거의 같은 좌표입니다.` })
      }
    }
  }

  if (data?.buildingEntranceNodeMap && typeof data.buildingEntranceNodeMap === 'object') {
    Object.entries(data.buildingEntranceNodeMap).forEach(([buildingId, nodeId]) => {
      if (!nodeById.has(String(nodeId))) errors.push({ field: 'buildingEntranceNodeMap', message: `${buildingId}의 출입구 노드(${nodeId})가 존재하지 않습니다.` })
    })
  }

  return { valid: errors.length === 0, errors, warnings }
}

// ── 청크 분할 / 체크섬 (배포 시 admin, 다운로드 시 앱이 동일 로직을 공유해야 한다) ──────────

/** nodes/edges를 하나의 정렬된 배열로 합쳐 청크 경계와 무관하게 항상 같은 체크섬이 나오게 한다. */
export function canonicalCampusGraphPayload({ nodes, edges }) {
  const sortedNodes = [...(nodes ?? [])].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const sortedEdges = [...(edges ?? [])].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  return JSON.stringify({ nodes: sortedNodes, edges: sortedEdges })
}

export async function computeCampusGraphChecksum(data) {
  const payload = canonicalCampusGraphPayload(data)
  const bytes = new TextEncoder().encode(payload)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('')
}

/** {nodes, edges}를 바이트 예산 내에서 그리디하게 청크 배열로 나눈다. 각 청크는 {items:[{t,v}]} 형태. */
export function chunkCampusGraphItems({ nodes, edges }, byteBudget = CAMPUS_GRAPH_CHUNK_BYTE_BUDGET) {
  const items = [
    ...(nodes ?? []).map(node => ({ t: 'n', v: node })),
    ...(edges ?? []).map(edge => ({ t: 'e', v: edge })),
  ]
  const chunks = []
  let current = []
  let currentSize = 2
  for (const item of items) {
    const itemSize = JSON.stringify(item).length + 1
    if (current.length > 0 && currentSize + itemSize > byteBudget) {
      chunks.push(current)
      current = []
      currentSize = 2
    }
    current.push(item)
    currentSize += itemSize
  }
  if (current.length > 0 || items.length === 0) chunks.push(current)
  return chunks
}

/** 청크 문서 배열(각 {index, items})을 다시 {nodes, edges}로 합친다. */
export function mergeCampusGraphChunks(chunkDocs) {
  const sorted = [...chunkDocs].sort((a, b) => a.index - b.index)
  const nodes = []
  const edges = []
  for (const chunk of sorted) {
    for (const item of chunk.items ?? []) {
      if (item.t === 'n') nodes.push(item.v)
      else if (item.t === 'e') edges.push(item.v)
    }
  }
  return { nodes, edges }
}
